import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { api, ApiError } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/Button";
import { ChevronLeftIcon } from "@/components/icons";
import { whatsappLink } from "@/lib/whatsapp";

// Flip this on once a payment provider (Stripe or M-Pesa) is actually wired up
// server-side — see functions/server/routes/checkout.ts, which rejects any
// "stripe" payment choice with a 503 while no provider is configured.
const ONLINE_PAYMENT_ENABLED = false;

export function Checkout() {
  const { items, totalCents, requiresDeposit, clear } = useCart();
  const navigate = useNavigate();
  const currency = items[0]?.currency ?? "KES";

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [paymentChoice, setPaymentChoice] = useState<"stripe" | "skip">(
    ONLINE_PAYMENT_ENABLED && requiresDeposit ? "stripe" : "skip",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blockedByDeposit = requiresDeposit && !ONLINE_PAYMENT_ENABLED;
  const depositOrderMessage = `Hi Allthingsnew! I'd like to order:\n${items
    .map((i) => `- ${i.qty} × ${i.name}`)
    .join("\n")}\n\nTotal: ${formatMoney(totalCents, currency)}${
    customerName ? `\n\nName: ${customerName}` : ""
  }${customerPhone ? `\nPhone: ${customerPhone}` : ""}`;

  if (items.length === 0) {
    navigate("/cart");
    return null;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.checkout.submit({
        items: items.map((i) => ({ productId: i.productId, qty: i.qty })),
        customerName,
        customerPhone,
        customerEmail: customerEmail || undefined,
        shippingAddress: "",
        city: "",
        notes: "",
        paymentChoice,
      });
      clear();
      if (res.stripeCheckoutUrl) {
        window.location.href = res.stripeCheckoutUrl;
      } else {
        navigate(`/order/${res.orderId}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-app flex-col bg-cream pb-32 sm:my-6 sm:min-h-[calc(100vh-3rem)] sm:rounded-3xl sm:border sm:border-stone-300 sm:shadow-raised">
      <div className="sticky top-0 z-20 flex items-center gap-3 bg-cream/95 px-5 py-5 backdrop-blur">
        <button type="button" onClick={() => navigate(-1)} aria-label="Back" className="text-ink">
          <ChevronLeftIcon />
        </button>
        <h1 className="font-display text-lg font-medium text-ink">Confirm Your Order</h1>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5 px-5">
        <p className="text-xs leading-relaxed text-stone-500">
          Just your contact details for now — we'll reach out by phone to confirm delivery.
        </p>
        <Field label="Full name" value={customerName} onChange={setCustomerName} required autoComplete="name" />
        <Field label="Phone number" value={customerPhone} onChange={setCustomerPhone} required autoComplete="tel" type="tel" />
        <Field
          label="Email (optional)"
          value={customerEmail}
          onChange={setCustomerEmail}
          autoComplete="email"
          type="email"
        />

        {ONLINE_PAYMENT_ENABLED && !blockedByDeposit && (
          <div className="flex flex-col gap-2">
            <PaymentOption
              selected={paymentChoice === "stripe"}
              onSelect={() => setPaymentChoice("stripe")}
              title="Pay now, online"
              subtitle="Secure card payment"
            />
            <PaymentOption
              selected={paymentChoice === "skip"}
              onSelect={() => setPaymentChoice("skip")}
              title="Pay on delivery / arrange separately"
              subtitle="We'll confirm payment with you directly"
            />
          </div>
        )}

        {error && <p className="text-sm text-error">{error}</p>}
      </form>

      <div className="fixed inset-x-0 bottom-0 z-30 mx-auto flex max-w-app flex-col gap-3 border-t border-stone-300 bg-white px-5 py-4 sm:absolute">
        <div className="flex items-center justify-between">
          <span className="text-sm text-stone-700">Total</span>
          <span className="text-base font-semibold text-ink">{formatMoney(totalCents, currency)}</span>
        </div>
        {blockedByDeposit ? (
          <a href={whatsappLink(depositOrderMessage)} target="_blank" rel="noopener noreferrer">
            <Button fullWidth type="button">
              Order via WhatsApp
            </Button>
          </a>
        ) : (
          <Button fullWidth disabled={submitting} onClick={handleSubmit}>
            {submitting ? "Placing order…" : "Place Order"}
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
  autoComplete,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  type?: string;
  autoComplete?: string;
  textarea?: boolean;
}) {
  const className =
    "rounded-lg border border-stone-300 bg-white px-3.5 py-3 text-sm text-ink placeholder:text-stone-500 focus:border-ink focus:outline-none";
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">{label}</span>
      {textarea ? (
        <textarea
          className={className}
          rows={3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        />
      ) : (
        <input
          className={className}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          autoComplete={autoComplete}
        />
      )}
    </label>
  );
}

function PaymentOption({
  selected,
  onSelect,
  title,
  subtitle,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        "flex flex-col items-start gap-0.5 rounded-lg border px-4 py-3 text-left",
        selected ? "border-ink bg-ink/5" : "border-stone-300 bg-white",
      ].join(" ")}
    >
      <span className="text-sm font-semibold text-ink">{title}</span>
      <span className="text-xs text-stone-500">{subtitle}</span>
    </button>
  );
}
